import { isNumber, round, toFinite } from "lodash";
import { ContractPensionModel, ContractType } from "@bonliva-crm/shared/enums";

const isNotZeroNumber = (x: any) => isNumber(x) && x !== 0;

export function getCustomerContractRecordPrice(
  basicPrice: number,
  priceMultiplier: number | null,
  priceExtra: number | null,
  customPrice: number | null
) {
  if (isNotZeroNumber(customPrice)) {
    return customPrice;
  }

  if (isNotZeroNumber(Number(basicPrice))) {
    if (isNotZeroNumber(Number(priceExtra))) {
      return round(toFinite(basicPrice) + toFinite(priceExtra), 2);
    }

    if (isNotZeroNumber(Number(priceMultiplier))) {
      return round(toFinite(basicPrice) * toFinite(priceMultiplier), 2);
    }
  }

  return null;
}

export function getConsultantContractRecordPrice(
  pensionModel: ContractPensionModel,
  basicPrice: number,
  priceMultiplier: number | null,
  priceExtra: number | null,
  customPrice: number | null,
  isNationalProcurementPriceStrategy: boolean,
  overLimit: boolean
): number | null {
  if (isNotZeroNumber(customPrice)) {
    return customPrice;
  }

  if (isNotZeroNumber(Number(basicPrice))) {
    const basicPriceApplied = applyNationalProcurementPriceStrategy(
      pensionModel,
      basicPrice,
      isNationalProcurementPriceStrategy,
      overLimit
    );

    if (isNotZeroNumber(Number(priceExtra))) {
      const priceExtraApplied = applyNationalProcurementPriceStrategy(
        pensionModel,
        priceExtra,
        isNationalProcurementPriceStrategy,
        overLimit
      );

      return round(toFinite(basicPriceApplied) + toFinite(priceExtraApplied), 2);
    }

    if (isNotZeroNumber(Number(priceMultiplier))) {
      return round(toFinite(basicPriceApplied) * toFinite(priceMultiplier), 2);
    }
  }

  return null;
}

export function applyNationalProcurementPriceStrategy(
  pensionModel: ContractPensionModel,
  basicPrice: number,
  isNationalProcurementPriceStrategy: boolean,
  overLimit: boolean
) {
  const pensionFeePercent = getPensionFeePercent(
    ContractType.Hourly,
    pensionModel,
    isNationalProcurementPriceStrategy,
    overLimit
  );

  const newPrice = basicPrice / (1 + pensionFeePercent / 100);

  return round(newPrice, 2);
}

/**
 * 7.5 × inkomstbasbelopp — the yearly gross salary (national procurement) at which the
 * pension provision goes from FEE_STANDARD to FEE_OVER_LIMIT.
 *
 * Fallback only. The authority is the `pensionThresholdByYear` CRM config key, which holds
 * one entry per year; this is used when that key has no entry for the year being checked.
 * Lives next to the fee schedule it belongs to so the two stay in sight of each other.
 */
export const ANNUAL_PENSION_THRESHOLD_FALLBACK = 625_500; // 7.5 × 83 400 (IBB 2026)

export function getPensionFeePercent(
  contractType: ContractType = ContractType.Hourly,
  pensionModel: ContractPensionModel = null,
  isNationalProcurementPriceStrategy: boolean,
  overLimit: boolean = false
): number {
  // Fee schedule
  const FEE_STANDARD = 4.5;
  const FEE_OVER_LIMIT = 30;

  if (isNationalProcurementPriceStrategy) {
    // Determine if pension amount exceeds the limit
    let isOverLimit = false;
    switch (pensionModel) {
      case ContractPensionModel.Annual:
        isOverLimit = overLimit;
        break;
      case ContractPensionModel.Months:
        isOverLimit = overLimit;
        break;
      default:
        isOverLimit = false;
        break;
    }

    return isOverLimit ? FEE_OVER_LIMIT : FEE_STANDARD;
  }

  if (
    contractType === ContractType.HourlyPension ||
    contractType === ContractType.HourlyPensionInsurance
  ) {
    return FEE_STANDARD;
  }

  return 0;
}
